import { Button, Divider, Segmented, SelectProps } from 'antd';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useState } from 'react';
import { useDebounce } from '@/hooks';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import {
  BankAccount,
  BankAccountFilter,
  BankAccountsQuery,
} from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import {
  useBankAccount,
  useBankAccountsFilter,
} from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: BankAccountsQuery;
  buttonSelectAll?: boolean;
  showActivityFilter?: boolean;
};

const BankAccountInfiniteSelectField: React.FC<Props> = ({
  queryFilter,
  buttonSelectAll = false,
  showActivityFilter = false,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState<string>();
  const [bankActive, setBankActive] = useState<boolean>(true);
  const search = useDebounce(searchValue);

  const { data, isLoading, hasNextPage, fetchNextPage } = useBankAccountsFilter(
    {
      active: bankActive,
      ...queryFilter,
      rows: 30,
      search,
      order: 'ASC',
    },
  );

  const bankAccounts = flatInfiniteData(data);
  const { data: detail } = useBankAccount(
    props.mode !== 'multiple' ? props.value : undefined,
  );

  const convertDetailToOption = (item: BankAccount): BankAccountFilter => ({
    id: item.id,
    bankBranch: item.bankBranch,
    bankCode: item.bank.code,
    bankId: item.bank.id,
    bankName: item.bank.name,
    bankNumber: item.number,
    description: item.description,
    verificationNumber: item.verificationNumber,
    branchVerificationNumber: item.branchVerificationNumber,
  });

  const options: BankAccountFilter[] =
    detail && !bankAccounts?.find((item) => item.id === detail.id)
      ? [...(bankAccounts || []), convertDetailToOption(detail)]
      : bankAccounts;

  function handleLabel(bankAccount: BankAccountFilter) {
    if (!bankAccount) return '';

    const {
      bankName = '',
      bankNumber = '',
      bankBranch = '',
      verificationNumber: verifyNumber,
      branchVerificationNumber: bVerifyNumber,
      description,
    } = bankAccount;

    const accountVerification = verifyNumber ? `/${verifyNumber}` : '';
    const branchVerification = bVerifyNumber ? `/${bVerifyNumber}` : '';
    const bankDescription = description ? `- ${description}` : '';

    return `${bankName} - ${bankNumber}${accountVerification} - ${bankBranch}${branchVerification} ${bankDescription}`;
  }

  const bankAccountOptions = options.map((cur) => ({
    value: cur.id,
    label: handleLabel(cur),
  }));

  const isMultiple = props.mode === 'multiple';

  return (
    <InfiniteSelect
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      loading={isLoading}
      options={bankAccountOptions}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      maxTagCount={'responsive'}
      maxTagTextLength={30}
      dropdownRender={(menu) => (
        <>
          {showActivityFilter && (
            <>
              <Segmented
                block
                className="segmented-boolean"
                value={String(bankActive)}
                onChange={(value) => setBankActive(value === 'true')}
                options={[
                  {
                    value: 'true',
                    label: 'Ativas',
                  },
                  {
                    value: 'false',
                    label: 'Inativas',
                  },
                ]}
              />

              <Divider style={{ margin: '8px 0' }} />
            </>
          )}

          {!!buttonSelectAll && isMultiple && (
            <>
              <Button
                block
                type="link"
                onClick={() => {
                  if (bankAccounts && props.onChange) {
                    props.onChange(
                      bankAccounts.map((bank) => bank.id),
                      bankAccounts.map((bank) => ({
                        value: bank.id,
                        label: handleLabel(bank),
                      })),
                    );
                  }
                }}
              >
                Selecionar Tudo
              </Button>

              <Divider style={{ margin: '8px 0' }} />
            </>
          )}

          {menu}
        </>
      )}
      {...props}
    />
  );
};

export default BankAccountInfiniteSelectField;
